import { supabase } from './supabase';

/*
  **How full each count sheet is, and what it is worth, in one request.**

  Written 8 September 2026, after the owner filmed signing in and watched a blank
  page with a spinner on it for about four seconds before his home appeared.

  **What it replaces.** Both the administrator's home and the manager's home used
  to read every line of the open cycle into the browser and add them up there.
  The open cycle has 2,530 lines and the database hands back about a thousand
  rows at a time, so that was three requests one after another, and 2,530 rows
  sent across for the sake of 23 numbers, with nothing at all on screen until the
  last of them arrived.

  The database does the adding up now, in `cycle_line_totals`. One request,
  twenty-three rows. Measured against the live database: ten milliseconds of
  database work, or 145 with the rules about who may see what applied, in place
  of three round trips.

  **The rules still apply, per person.** The function is SECURITY INVOKER, which
  means the database checks who is asking exactly as it did for the reads this
  replaces. Proved against the live database on 8 September 2026: the owner and
  Charlie Cude, who covers Dallas, both get all 23 sheets; Jamy Stull, who covers
  Louisiana, Oklahoma and San Antonio, gets none, and none of the 23 are hers.

  **A failed read is not a set of zeroes.** It says so, so a caller can leave the
  numbers alone rather than print a confident nought.
*/
export async function cycleLineTotals(cycleId, onlyIds = null) {
  const empty = { lineCounts: {}, valuations: {}, cycleValue: null, failed: false };
  if (!cycleId) return empty;

  const { data, error } = await supabase.rpc('cycle_line_totals', { p_cycle_id: cycleId });
  if (error) return { ...empty, failed: true };

  const keep = onlyIds ? new Set(onlyIds) : null;
  const lineCounts = {};
  const valuations = {};
  let cycleTotal = 0;

  for (const row of data || []) {
    const id = row.inventory_count_id;
    if (keep && !keep.has(id)) continue;
    // Postgres counts and sums arrive as strings once they are big enough to
    // need to be, so every one of them is turned into a number here rather than
    // being added to a total as text.
    const total = Number(row.lines_total) || 0;
    const done  = Number(row.lines_done)  || 0;
    const value = Number(row.value)       || 0;
    lineCounts[id] = { done, total };
    valuations[id] = value;
    cycleTotal += value;
  }

  return { lineCounts, valuations, cycleValue: cycleTotal > 0 ? cycleTotal : null, failed: false };
}
